import React, { lazy } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import { ScrollReveal } from '../components/ScrollReveal';
import { NextPageLink } from '../components/NextPageLink';
import { useSEO } from '../utils/seo';
import { blogPosts } from '../data/blogPosts';
export const Blog = () => {
  useSEO({
    title: 'Blog & Actualités | Détatouage Laser Martigny | Dispel Your Tattoo',
    description: 'Découvrez nos articles sur le détatouage laser, les technologies picoseconde, et nos conseils pour un traitement optimal à Martigny.',
    canonical: 'https://www.dispelyourtattoo.ch/blog'
  });
  const featuredPost = blogPosts[0];
  const remainingPosts = blogPosts.slice(1);
  return <main className="pt-32 pb-0 bg-brand-dark min-h-screen" data-id="element-277">
      {/* Header Section */}
      <section className="py-16 md:py-24 px-6 md:px-12 max-w-[1400px] mx-auto text-center" data-id="element-278">
        <ScrollReveal data-id="element-279">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-medium text-brand-light mb-8 tracking-tight text-balance" data-id="element-280">
            Le Blog
          </h1>
        </ScrollReveal>
        <ScrollReveal delay={0.1} data-id="element-281">
          <p className="text-lg md:text-xl text-brand-gray font-light leading-relaxed max-w-2xl mx-auto" data-id="element-282">
            Actualités, conseils et informations détaillées sur le détatouage
            laser et nos technologies.
          </p>
        </ScrollReveal>
      </section>

      {/* Featured Post */}
      <section className="px-6 md:px-12 max-w-[1400px] mx-auto mb-16 md:mb-24" data-id="element-283">
        <ScrollReveal delay={0.2} data-id="element-284">
          <Link to={`/blog/${featuredPost.slug}`} className="group block relative rounded-3xl overflow-hidden border border-brand-border/30 bg-brand-darker" data-id="element-285">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0" data-id="element-286">
              <div className="relative h-[300px] md:h-[400px] lg:h-[600px] overflow-hidden" data-id="element-287">
                <motion.img whileHover={{
                scale: 1.05
              }} transition={{
                duration: 0.6,
                ease: [0.16, 1, 0.3, 1]
              }} src={featuredPost.featuredImage} alt={featuredPost.title} className="w-full h-full object-cover grayscale-[20%]" data-id="element-288" />
                <div className="absolute top-4 md:top-6 left-4 md:left-6 bg-brand-dark/80 backdrop-blur-md px-3 md:px-4 py-1 md:py-1.5 rounded-full text-[10px] md:text-xs font-bold text-brand-light uppercase tracking-wider border border-brand-border/50" data-id="element-289">
                  À la une
                </div>
              </div>
              <div className="p-6 md:p-10 lg:p-16 flex flex-col justify-center" data-id="element-290">
                <div className="flex items-center space-x-3 md:space-x-4 text-xs md:text-sm text-brand-gray mb-4 md:mb-6 font-light" data-id="element-291">
                  <div className="flex items-center space-x-1.5" data-id="element-292">
                    <Calendar size={14} data-id="element-293" />
                    <span data-id="element-294">{featuredPost.date}</span>
                  </div>
                  <div className="w-1 h-1 rounded-full bg-brand-border" data-id="element-295" />
                  <div className="flex items-center space-x-1.5" data-id="element-296">
                    <Clock size={14} data-id="element-297" />
                    <span data-id="element-298">{featuredPost.readTime}</span>
                  </div>
                </div>
                <h2 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-medium text-brand-light mb-4 md:mb-6 tracking-tight group-hover:text-brand-gray transition-colors duration-300 text-balance" data-id="element-299">
                  {featuredPost.title}
                </h2>
                <p className="text-brand-gray text-base md:text-lg leading-relaxed mb-8 md:mb-10 font-light line-clamp-3" data-id="element-300">
                  {featuredPost.excerpt}
                </p>
                <div className="inline-flex items-center space-x-2 text-brand-light font-bold uppercase tracking-wider text-xs md:text-sm group-hover:translate-x-2 transition-transform duration-300" data-id="element-301">
                  <span data-id="element-302">Lire l'article</span>
                  <ArrowRight size={16} data-id="element-303" />
                </div>
              </div>
            </div>
          </Link>
        </ScrollReveal>
      </section>

      {/* Remaining Posts Grid */}
      <section className="px-6 md:px-12 max-w-[1400px] mx-auto pb-24 md:pb-32" data-id="element-304">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-12 md:gap-y-16 lg:gap-y-24" data-id="element-305">
          {remainingPosts.map((post, index) => <ScrollReveal key={post.slug} delay={index * 0.1} data-id="element-306">
              <Link to={`/blog/${post.slug}`} className={`group block ${index % 2 === 1 ? 'md:mt-16 lg:mt-24' : ''}`} data-id="element-307">
                <div className="relative rounded-2xl overflow-hidden mb-8 border border-brand-border/30 aspect-[4/3]" data-id="element-308">
                  <motion.img whileHover={{
                scale: 1.05
              }} transition={{
                duration: 0.6,
                ease: [0.16, 1, 0.3, 1]
              }} src={post.featuredImage} alt={post.title} className="w-full h-full object-cover grayscale-[20%]" loading="lazy" data-id="element-309" />
                </div>
                <div className="flex items-center space-x-4 text-xs text-brand-gray mb-4 font-light" data-id="element-310">
                  <div className="flex items-center space-x-1.5" data-id="element-311">
                    <Calendar size={14} data-id="element-312" />
                    <span data-id="element-313">{post.date}</span>
                  </div>
                  <div className="w-1 h-1 rounded-full bg-brand-border" data-id="element-314" />
                  <div className="flex items-center space-x-1.5" data-id="element-315">
                    <Clock size={14} data-id="element-316" />
                    <span data-id="element-317">{post.readTime}</span>
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-brand-light mb-4 tracking-tight group-hover:text-brand-gray transition-colors duration-300 text-balance" data-id="element-318">
                  {post.title}
                </h3>
                <p className="text-brand-gray leading-relaxed font-light line-clamp-2 mb-6" data-id="element-319">
                  {post.excerpt}
                </p>
                <div className="inline-flex items-center space-x-2 text-brand-light font-bold uppercase tracking-wider text-xs group-hover:translate-x-2 transition-transform duration-300" data-id="element-320">
                  <span data-id="element-321">Lire l'article</span>
                  <ArrowRight size={14} data-id="element-322" />
                </div>
              </Link>
            </ScrollReveal>)}
        </div>
      </section>

      <NextPageLink data-id="element-323" />
    </main>;
};